import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import { useParams, useNavigate } from "react-router-dom";
import { Tooltip, LoadingSkeleton } from "@narmi/design_system";
import { entities, dollars } from "byzantine";
import Filters from "byzantine/src/filters";
import useCardLinkedOffersFeature from "../CardLinkedOffers/hooks";
import CardSelector from "../subscriptions/CardSelector";
import CardOfferDetailsDrawer from "../CardLinkedOffers/CardOfferDetailsDrawer";
import CardLinkedOffersUnenrollModal from "../CardLinkedOffers/UnenrollModal";
import { CardOffersActivationModal } from "../CardLinkedOffers/ActivationModal";
import { useSubscriptionCards } from "../contexts/SubscriptionCardsContext";
import EnrolledUserOfferContent from "./EnrolledUserOfferContent";

import styles from "./CardOffersDashboard.module.scss";
import PageLayout from "../../src/components/PageLayout";

const CardOffersDashboard = () => {
  const { l10n } = useLocalization();
  const { cardUuid } = useParams<{ cardUuid?: string }>();
  const { cards, isLoading: isCardsLoading } = useSubscriptionCards();
  const navigate = useNavigate();
  const isEnabled = useCardLinkedOffersFeature();
  const [selectedOfferId, setSelectedOfferId] = useState<string | undefined>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isUnenrollModalOpen, setIsUnenrollModalOpen] = useState(false);
  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);

  // Data fetching hooks
  const { fetchOffers, loading } =
    entities.cardLinkedOffers.useFetchCardLinkedOffers();
  const { fetchEnrollmentStatus, loading: enrollmentLoading } =
    entities.cardLinkedOffers.useFetchCardLinkedOffersEnrollment();
  const isUserEnrolled =
    entities.cardLinkedOffers.useCardLinkedOffersEnrollmentStatus();

  // Fetch enrollment status and offers data on mount
  useEffect(() => {
    if (isEnabled) {
      fetchEnrollmentStatus({
        onData: ({ is_enrolled: isEnrolled }) => {
          if (!isEnrolled) {
            setIsActivationModalOpen(true);
          } else {
            fetchOffers(false); // Available offers
            fetchOffers(true); // Recommended offers
          }
        },
      });
    }
  }, [isEnabled, fetchEnrollmentStatus, fetchOffers]);

  const cardId = useMemo<API.CardId>(() => {
    if (!cardUuid && cards.length > 0) {
      return cards[0].id;
    }
    return cardUuid as API.CardId;
  }, [cardUuid, cards]);

  const handleOfferClick = useCallback((offerId: string) => {
    setSelectedOfferId(offerId);
    setIsDrawerOpen(true);
  }, []);

  const handleDrawerDismiss = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedOfferId(undefined);
  }, []);

  const handleUnenrollClick = useCallback(() => {
    setIsUnenrollModalOpen(true);
  }, []);

  const handleUnenrollModalClose = useCallback(() => {
    setIsUnenrollModalOpen(false);
  }, [navigate]);

  const handleUnenrollSuccess = useCallback(() => {
    setIsUnenrollModalOpen(false);
    navigate(-1);
  }, [navigate]);

  const handleActivationModalClose = useCallback(() => {
    if (isActivationModalOpen) {
      setIsActivationModalOpen(false);
      navigate(-1);
    }
  }, [isActivationModalOpen]);

  const handleSuccessfulEnrollment = useCallback(() => {
    setIsActivationModalOpen(false);
    // Refetch offers after successful enrollment
    fetchOffers(false); // Available offers
    fetchOffers(true); // Recommended offers
  }, [fetchOffers]);

  const handleCardChange = useCallback(
    (newCardId: API.CardId) => {
      navigate(`/cards/${newCardId}/offers`);
    },
    [navigate]
  );

  // TODO: Replace with accurate data.
  const offersUsed = 0;
  const cashBackEarnings = "0";

  if (!isCardsLoading && (!cards || !cards.length)) {
    navigate(-1);
  }

  if (!cardId || !isEnabled) {
    return null;
  }

  const isLoading = loading || enrollmentLoading;

  return (
    <PageLayout>
      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Page Title and Card Selector */}
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>
            {l10n.getString(
              "card-offers-dashboard-page-title",
              null,
              "Card offers"
            )}
          </h1>
          <div className={styles.cardSelectorContainer}>
            <CardSelector
              cardId={cardId}
              cards={cards}
              onChange={handleCardChange}
            />
          </div>
        </div>

        <LoadingSkeleton isLoading={isLoading}>
          {/* Annual Earnings Card */}
          <div className={styles.lifetimeEarningsCard}>
            <div className={styles.earningsHeader}>
              <div className={styles.earningsLabelContainer}>
                <div className={styles.earningsLabel}>
                  {l10n.getString(
                    "card-offers-dashboard-annual-earnings",
                    null,
                    "Annual earnings"
                  )}
                </div>
                <Tooltip
                  text={l10n.getString(
                    "card-offers-dashboard-annual-earnings-tooltip",
                    null,
                    "Total cash back earnings this year for this card."
                  )}
                >
                  <i className={`narmi-icon-info ${styles.infoIcon}`} />
                </Tooltip>
              </div>
              <div className={styles.earningsAmount}>
                {Filters.currency(dollars(cashBackEarnings), {
                  hasDecimal: true,
                })}
              </div>
            </div>

            {offersUsed > 0 && (
              <>
                <div className={styles.earningsDivider} />
                <div className={styles.offersUsedSection}>
                  <div className={styles.offersUsedLabel}>
                    {l10n.getString(
                      "card-offers-dashboard-offers-used",
                      null,
                      "Offers used this month"
                    )}
                  </div>
                  <span className={styles.offersUsedBadge}>{offersUsed}</span>
                </div>
              </>
            )}
          </div>

          {/* Only show offers content if user is enrolled */}
          {isUserEnrolled && (
            <EnrolledUserOfferContent
              onOfferClick={handleOfferClick}
              onUnenrollClick={handleUnenrollClick}
            />
          )}
        </LoadingSkeleton>
      </div>

      {/* Offer Details Drawer */}
      <CardOfferDetailsDrawer
        isOpen={isDrawerOpen}
        offerId={selectedOfferId}
        onUserDismiss={handleDrawerDismiss}
        onOfferClick={handleOfferClick}
      />

      {/* Unenroll Modal */}
      <CardLinkedOffersUnenrollModal
        isOpen={isUnenrollModalOpen}
        onSuccess={handleUnenrollSuccess}
        onClose={handleUnenrollModalClose}
        testId="card-offers-unenroll-modal"
      />

      {/* Activation Modal */}
      <CardOffersActivationModal
        isOpen={isActivationModalOpen}
        onClose={handleActivationModalClose}
        onSuccess={handleSuccessfulEnrollment}
        testId="card-offers-activation-modal"
      />
    </PageLayout>
  );
};

export default CardOffersDashboard;
